import { useEffect, useRef, type RefObject } from 'react';
import type { WidgetId } from '@rabbitfuel/shared';
import { ZOOM_STEPS, type LayerZoom, type ViewerLayer } from '../lib/viewerLayers.ts';

export interface LayerItem {
  id: ViewerLayer;
  label: string;
  /** Zweite Zeile, z. B. welche Kamera gerade im kleinen Fenster liegt. */
  hint?: string;
  /** Eine Einblendung der Regie -- die laesst sich auch groesser oder kleiner stellen. */
  zoomable?: boolean;
}

/**
 * Die Schalter, mit denen ein Zuschauer Einblendungen fuer sich ausblendet,
 * und je Einblendung − und +, um sie groesser oder kleiner zu sehen.
 *
 * Auf dem Handy hochkant steht die Liste in der Seite direkt unter der
 * Bedienleiste, sonst klappt sie ueber dem Bild unter den Knoepfen auf. Ein
 * Tipp daneben oder Escape schliesst sie.
 */
export function LayerMenu({
  items,
  hidden,
  onToggle,
  onAll,
  onClose,
  anchorRef,
  zoom = {},
  onZoom,
  onZoomReset,
  className = '',
}: {
  items: LayerItem[];
  hidden: ReadonlySet<ViewerLayer>;
  onToggle: (id: ViewerLayer) => void;
  onAll: (show: boolean) => void;
  onClose: () => void;
  zoom?: LayerZoom;
  onZoom?: (id: WidgetId, direction: 1 | -1) => void;
  onZoomReset?: () => void;
  /** Der Knopf, der die Liste oeffnet. */
  anchorRef: RefObject<HTMLElement | null>;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onPointer = (e: PointerEvent) => {
      const target = e.target as Node;
      // Der Knopf schliesst die Liste selbst -- sonst ginge sie hier zu und
      // mit seinem Klick sofort wieder auf.
      if (ref.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, anchorRef]);

  const anyShown = items.some((item) => !hidden.has(item.id));
  const zoomed = items.some((item) => item.zoomable && (zoom[item.id as WidgetId] ?? 1) !== 1);

  return (
    <div ref={ref} className={`layer-menu ${className}`} role="group" aria-label="Einblendungen auf diesem Gerät">
      <div className="layer-menu__head">
        <b>Einblendungen</b>
        <button type="button" className="layer-menu__all" onClick={() => onAll(!anyShown)}>
          {anyShown ? 'Alle aus' : 'Alle an'}
        </button>
      </div>
      <ul className="layer-menu__list">
        {items.map((item) => {
          const on = !hidden.has(item.id);
          // Groesse nur fuer das, was gerade zu sehen ist -- eine ausgeblendete
          // Einblendung groesser zu stellen, zeigte nichts.
          const sized = on && item.zoomable && onZoom ? (zoom[item.id as WidgetId] ?? 1) : null;
          return (
            <li key={item.id}>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                className="layer-menu__row"
                onClick={() => onToggle(item.id)}
              >
                <span className="layer-menu__label">
                  {item.label}
                  {item.hint && <small>{item.hint}</small>}
                </span>
                <span className={`layer-switch${on ? ' is-on' : ''}`} aria-hidden />
              </button>
              {sized !== null && onZoom && (
                <div className="layer-size" role="group" aria-label={`Größe ${item.label}`}>
                  <span className="layer-size__label">Größe</span>
                  <button
                    type="button"
                    className="layer-size__btn"
                    onClick={() => onZoom(item.id as WidgetId, -1)}
                    disabled={sized <= ZOOM_STEPS[0]}
                    aria-label={`${item.label} kleiner`}
                  >
                    −
                  </button>
                  <output className="layer-size__value" aria-live="polite">
                    {Math.round(sized * 100)} %
                  </output>
                  <button
                    type="button"
                    className="layer-size__btn"
                    onClick={() => onZoom(item.id as WidgetId, 1)}
                    disabled={sized >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                    aria-label={`${item.label} größer`}
                  >
                    +
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <div className="layer-menu__foot">
        <span>Gilt nur auf diesem Gerät.</span>
        {zoomed && onZoomReset && (
          <button type="button" className="layer-menu__reset" onClick={onZoomReset}>
            Größen zurücksetzen
          </button>
        )}
      </div>
    </div>
  );
}

/** Drei gestapelte Ebenen -- das Symbol fuer „Einblendungen". */
export function LayersIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      width="17"
      height="17"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 2.5 2.5 6.5 10 10.5l7.5-4L10 2.5Z" />
      <path d="m2.5 10 7.5 4 7.5-4" />
      <path d="m2.5 13.5 7.5 4 7.5-4" />
    </svg>
  );
}
